import {
    TRIGGER_END_DOWNLOAD_CART_ELEMENTS_EVENT,
    TRIGGER_RESPONSE_CART_EVENT,
} from '@core/constants/EVENTS';
import { SIGNAL_IS_ABORTED_CODE, USER_NOT_FOUND_CODE } from '@core/constants/API';
import { NOTIFICATIONS_TYPE } from '@core/constants/NOTIFICATIONS';
import { ROUTES } from '@core/constants/ROUTES';
import { cleanCartState, getCartState, updateCartState, downloadCart } from './api';
import { relaunchQuery401or403status } from './utils';
import { delayTranslate, translate as t } from '@core/i18n/window-language';
import { showNotification } from './notification-messages';
import { goToHomeView } from '@core/utils/navigate';
import { checkValidAccessToken } from './oauth';
import { getWesogUserInfo, isUserLoggedIn, updateWesogUserInfo } from './wesog-user';
import { WESOG_USER_PARAMS } from '@core/constants/USER';

/**
 * Update the cart local store.
 * @param {object[]} cartElements - The cart elements.
 * @returns {void}
 * @private
 */
const _updateCartLocalStore = (cartElements) => {
    const /** @type {Object} */ user = getWesogUserInfo();
    const /** @type {string[]} */ cartUidElements = cartElements
            .map((element) => element.uid)
            .filter((uid) => uid);
    user[WESOG_USER_PARAMS.CART_UIDS] = cartUidElements;

    updateWesogUserInfo(user);
};

/**
 * Check the current uids to add.
 * @param {string[]} currentUids - The current uids.
 * @returns {string[]}
 * @private
 */
const _checkCurrentUidToAdd = (currentUids, isItem = true) => {
    const user = getWesogUserInfo();
    const userUids = user[WESOG_USER_PARAMS.CART_UIDS] || [];

    const elementsToNotAdd = currentUids.filter((uid) => userUids.includes(uid));

    if (elementsToNotAdd.length) {
        if (isItem) {
            showNotification(
                t('notification.warning.elementToNotAddCart'),
                NOTIFICATIONS_TYPE.WARNING,
            );
        } else {
            showNotification(
                t('notification.warning.elementsToNotAddCart'),
                NOTIFICATIONS_TYPE.WARNING,
            );
        }
    }

    return currentUids.filter((uid) => !userUids.includes(uid));
};

/**
 * Get the Cart list.
 * @param {object} [params={}] - The parameters object.
 * @param {number} [params.times=0] - The number of times the function has been relaunched. Default is 0.
 * @returns {Promise<void>}
 */
export const getCartList = async ({ times = 0 } = {}) => {
    if (!isUserLoggedIn()) {
        return;
    }

    try {
        await checkValidAccessToken();

        const /** @type {Response} */ response = await getCartState(true);

        if (!response.ok) {
            const wasRelaunched = await relaunchQuery401or403status(
                response,
                { times },
                getCartList,
            );

            // If the function was relaunched, return early to prevent duplicate processing
            if (wasRelaunched) {
                return;
            }

            // If not relaunched and still not ok, return early
            return;
        }

        const /** @type {object} */ responseJson = await response.json();

        _updateCartLocalStore(responseJson?.elements || []);

        document.dispatchEvent(
            new CustomEvent(TRIGGER_RESPONSE_CART_EVENT, {
                detail: responseJson,
            }),
        );
    } catch (error) {
        /* If the error is because the signal is aborted, we don't show the notification */
        if (error?.message?.includes(SIGNAL_IS_ABORTED_CODE)) {
            return;
        }

        console.error('Error cart list method', error);

        if (error?.message?.includes(USER_NOT_FOUND_CODE)) {
            delayTranslate(() => {
                showNotification(
                    t('notification.errors.userNotFound'),
                    NOTIFICATIONS_TYPE.WARNING,
                );
            });

            if (window.location.pathname.includes(ROUTES.CART_LIST)) {
                setTimeout(() => {
                    goToHomeView();
                }, 3500);
            }
        } else {
            delayTranslate(() => {
                showNotification(
                    t('notification.errors.getCollectionsError'),
                    NOTIFICATIONS_TYPE.ERROR,
                );
            });
        }
    }
};

/**
 * Add an element to the cart.
 * @param {string} uidElement - The element uid.
 * @param {boolean} isItem - The element is an item.
 * @returns {Promise<void>}
 */
export const addCartElement = async (uidElement, isItem = true) => {
    await checkValidAccessToken();

    const /** @type {string[]} */ elementToAdd = _checkCurrentUidToAdd(
            [uidElement],
            isItem,
        );

    if (!elementToAdd.length) {
        return;
    }

    try {
        const /** @type {Response} */ response = await updateCartState(
                uidElement,
                isItem,
                true,
            );

        if (!response.ok) {
            if (response.status === 406) {
                delayTranslate(() => {
                    showNotification(
                        t('notification.warning.maxElementsCart'),
                        NOTIFICATIONS_TYPE.WARNING,
                    );
                });
            }

            throw Error('Error adding the element to the cart');
        }

        const /** @type {object} */ responseJson = await response.json();

        const /** @type {Object[]} */ currentElements = responseJson?.elements || [];
        _updateCartLocalStore(currentElements);

        document.dispatchEvent(
            new CustomEvent(TRIGGER_RESPONSE_CART_EVENT, {
                detail: {
                    elements: currentElements,
                },
            }),
        );

        showNotification(
            t('notification.success.addCartElement'),
            NOTIFICATIONS_TYPE.SUCCESS,
        );
    } catch (error) {
        /* If the error is because the signal is aborted, we don't show the notification */
        if (error?.message?.includes(SIGNAL_IS_ABORTED_CODE)) {
            return;
        }

        console.error('Error add cart elements method', error);

        if (error?.message?.includes(USER_NOT_FOUND_CODE)) {
            delayTranslate(() => {
                showNotification(
                    t('notification.errors.userNotFound'),
                    NOTIFICATIONS_TYPE.WARNING,
                );
            });
        } else {
            delayTranslate(() => {
                showNotification(
                    t('notification.errors.errorAddCartElement'),
                    NOTIFICATIONS_TYPE.ERROR,
                );
            });
        }
    }

    return;
};

/**
 * Add elements to the cart.
 * @param {string} reportUid - The report uid.
 * @returns {Promise<void>}
 */
export const addCartElements = async (reportUid) => {
    await checkValidAccessToken();

    try {
        const /** @type {Response} */ response = await updateCartState(
                reportUid,
                false,
                true,
            );

        if (!response.ok) {
            if (response.status === 406) {
                delayTranslate(() => {
                    showNotification(
                        t('notification.warning.maxElementsCart'),
                        NOTIFICATIONS_TYPE.WARNING,
                    );
                });
            }

            throw Error('Error adding report to the cart');
        }

        const /** @type {object} */ responseJson = await response.json();

        const /** @type {Object[]} */ currentElements = responseJson?.elements || [];
        _updateCartLocalStore(currentElements);

        document.dispatchEvent(
            new CustomEvent(TRIGGER_RESPONSE_CART_EVENT, {
                detail: {
                    elements: currentElements,
                },
            }),
        );

        showNotification(
            t('notification.success.addCartElement'),
            NOTIFICATIONS_TYPE.SUCCESS,
        );
    } catch (error) {
        /* If the error is because the signal is aborted, we don't show the notification */
        if (error?.message?.includes(SIGNAL_IS_ABORTED_CODE)) {
            return;
        }

        console.error('Error add cart elements method', error);

        if (error?.message?.includes(USER_NOT_FOUND_CODE)) {
            delayTranslate(() => {
                showNotification(
                    t('notification.errors.userNotFound'),
                    NOTIFICATIONS_TYPE.WARNING,
                );
            });
        } else {
            delayTranslate(() => {
                showNotification(
                    t('notification.errors.errorAddCartElement'),
                    NOTIFICATIONS_TYPE.ERROR,
                );
            });
        }
    }

    return;
};

/**
 * Remove an element to the cart.
 * @param {string} uidElement - The element uid.
 * @param {boolean} isItem - The element is an item.
 * @returns {Promise<void>}
 */
export const removeCartElement = async (uidElement, isItem = true) => {
    await checkValidAccessToken();

    try {
        const /** @type {Response} */ response = await updateCartState(
                uidElement,
                isItem,
                false,
            );

        if (!response.ok) {
            throw Error('Error removing the element to the cart');
        }

        const /** @type {object} */ responseJson = await response.json();

        const /** @type {Object[]} */ currentElements = responseJson?.elements || [];
        _updateCartLocalStore(currentElements);

        document.dispatchEvent(
            new CustomEvent(TRIGGER_RESPONSE_CART_EVENT, {
                detail: {
                    elements: currentElements,
                },
            }),
        );

        showNotification(
            t('notification.success.removeCartElement'),
            NOTIFICATIONS_TYPE.SUCCESS,
        );
    } catch (error) {
        /* If the error is because the signal is aborted, we don't show the notification */
        if (error?.message?.includes(SIGNAL_IS_ABORTED_CODE)) {
            return;
        }

        console.error('Error remove cart elements method', error);

        if (error?.message?.includes(USER_NOT_FOUND_CODE)) {
            delayTranslate(() => {
                showNotification(
                    t('notification.errors.userNotFound'),
                    NOTIFICATIONS_TYPE.WARNING,
                );
            });
        } else {
            delayTranslate(() => {
                showNotification(
                    t('notification.errors.errorRemoveCartElement'),
                    NOTIFICATIONS_TYPE.ERROR,
                );
            });
        }
    }

    return;
};

/**
 * Remove an element to the cart.
 * @returns {Promise<void>}
 */
export const cleanCartElements = async () => {
    await checkValidAccessToken();

    try {
        const /** @type {Response} */ response = await cleanCartState();

        if (!response.ok) {
            throw Error('Error removing the element to the cart');
        }

        const /** @type {object} */ responseJson = await response.json();

        const /** @type {Object[]} */ currentElements = responseJson?.elements || [];
        _updateCartLocalStore(currentElements);

        document.dispatchEvent(
            new CustomEvent(TRIGGER_RESPONSE_CART_EVENT, {
                detail: {
                    elements: currentElements,
                },
            }),
        );

        showNotification(
            t('notification.success.removeCartElements'),
            NOTIFICATIONS_TYPE.SUCCESS,
        );
    } catch (error) {
        /* If the error is because the signal is aborted, we don't show the notification */
        if (error?.message?.includes(SIGNAL_IS_ABORTED_CODE)) {
            return;
        }

        console.error('Error remove cart elements method', error);

        if (error?.message?.includes(USER_NOT_FOUND_CODE)) {
            delayTranslate(() => {
                showNotification(
                    t('notification.errors.userNotFound'),
                    NOTIFICATIONS_TYPE.WARNING,
                );
            });
        } else {
            delayTranslate(() => {
                showNotification(
                    t('notification.errors.errorRemoveCartElements'),
                    NOTIFICATIONS_TYPE.ERROR,
                );
            });
        }
    }

    return;
};

/**
 * Empty the cart state.
 * @returns {void}
 */
export const emptyCartState = () => {
    document.dispatchEvent(
        new CustomEvent(TRIGGER_RESPONSE_CART_EVENT, {
            detail: {
                elements: [],
            },
        }),
    );
};

/**
 * Get the Cart list.
 * @param {object} [params={}] - The parameters object.
 * @param {number} [params.times=0] - The number of times the function has been relaunched. Default is 0.
 * @returns {Promise<void>}
 */
export const getCartListCompleted = async ({ times = 0 } = {}) => {
    await checkValidAccessToken();

    if (!isUserLoggedIn()) {
        return;
    }

    try {
        const /** @type {Response} */ response = await getCartState(true);

        if (!response.ok) {
            const wasRelaunched = await relaunchQuery401or403status(
                response,
                { times },
                getCartListCompleted,
            );

            // If the function was relaunched, return early to prevent duplicate processing
            if (wasRelaunched) {
                return;
            }

            // If not relaunched and still not ok, return early
            return;
        }

        const /** @type {object} */ responseJson = await response.json();

        _updateCartLocalStore(responseJson?.elements || []);

        document.dispatchEvent(
            new CustomEvent(TRIGGER_RESPONSE_CART_EVENT, {
                detail: responseJson,
            }),
        );
    } catch (error) {
        /* If the error is because the signal is aborted, we don't show the notification */
        if (error?.message?.includes(SIGNAL_IS_ABORTED_CODE)) {
            return;
        }

        console.error('Error cart list method', error);

        if (error?.message?.includes(USER_NOT_FOUND_CODE)) {
            delayTranslate(() => {
                showNotification(
                    t('notification.errors.userNotFound'),
                    NOTIFICATIONS_TYPE.WARNING,
                );
            });

            if (window.location.pathname.includes(ROUTES.CART_LIST)) {
                setTimeout(() => {
                    goToHomeView();
                }, 3500);
            }
        } else {
            delayTranslate(() => {
                showNotification(
                    t('notification.errors.getCollectionsError'),
                    NOTIFICATIONS_TYPE.ERROR,
                );
            });
        }
    }
};

/**
 * Download the cart elements.
 * @param {{ fieldid: string}[]} cartOptions - The cart options.
 * @returns {Promise<void>}
 */
export const downloadCartElements = async (cartOptions) => {
    await checkValidAccessToken();

    if (!isUserLoggedIn() || !cartOptions.length) {
        return;
    }

    const /** @type {string[]} */ elementsToDownload = cartOptions.map((option) => {
            return {
                fileid: option.fileid,
            };
        });

    downloadCart(elementsToDownload)
        .then(async (response) => {
            if (!response.ok) {
                throw Error('Error downloading the cart elements');
            }

            const /** @type {object} */ responseBlob = await response.blob();

            const /** @type {string} */ url = window.URL.createObjectURL(responseBlob);
            const /** @type {HTMLAnchorElement} */ link = document.createElement('a');

            // get the filename from the content-disposition
            const /** @type {string} */ contentDisposition =
                    response.headers.get('content-disposition');
            const /** @type {string} */ filename = contentDisposition
                    .split(';')
                    .find((item) => item.includes('filename='))
                    .split('=')[1]
                    .trim();

            link.href = url;
            link.download = filename;
            link.click();

            link.remove();

            document.dispatchEvent(
                new CustomEvent(TRIGGER_END_DOWNLOAD_CART_ELEMENTS_EVENT),
            );
        })
        .catch((error) => {
            /* If the error is because the signal is aborted, we don't show the notification */
            if (error?.message?.includes(SIGNAL_IS_ABORTED_CODE)) {
                return;
            }

            console.error('Error download cart elements method', error);

            if (error?.message?.includes(USER_NOT_FOUND_CODE)) {
                delayTranslate(() => {
                    showNotification(
                        t('notification.errors.userNotFound'),
                        NOTIFICATIONS_TYPE.WARNING,
                    );
                });

                if (window.location.pathname.includes(ROUTES.CART_LIST)) {
                    setTimeout(() => {
                        goToHomeView();
                    }, 3500);
                }
            } else {
                delayTranslate(() => {
                    showNotification(
                        t('notification.errors.errorDownloadCartElements'),
                        NOTIFICATIONS_TYPE.ERROR,
                    );
                });
            }
        });
};
